import type { OrgModel } from '@tive/core';

export const CARD_WIDTH = 252;
/** Tall enough for a name, a two-line title, where they sit, and the counts. */
export const CARD_HEIGHT = 104;

/** One layer down. */
const ROW_GAP = 62;
const ROW_PITCH = CARD_HEIGHT + ROW_GAP;
/**
 * Between stacked cards in a column — tighter than a row, because they read as
 * one list, but wide enough for the expand pill that straddles a card's edge.
 */
const STACK_GAP = 28;
const STACK_PITCH = CARD_HEIGHT + STACK_GAP;
/** Between the two columns of a stack; the trunk runs down the middle of it. */
const COLUMN_GAP = 30;
/** Between a parent's blocks: its stack, and each report that is expanded. */
const BLOCK_GAP = 72;
/** Gap between separate root trees, when the data has more than one. */
const ROOT_TREE_GAP = 160;

/**
 * More reports than this and they stack into columns instead of spreading out.
 * Two side by side is narrower than two stacked, so stacking below that would
 * cost height for nothing.
 */
export const STACK_THRESHOLD = 2;
/** Columns a stack is dealt into, filled top to bottom. */
const STACK_COLUMNS = 2;

export interface Placement {
  x: number;
  y: number;
}

/**
 * How a report is joined to its manager.
 *
 * `spread` is the classic elbow from the bottom of the manager's card into the
 * top of the report's. `stack` is the list form: a trunk dropped between the
 * two columns with a stub into the side of each card.
 */
export interface LayoutEdge {
  id: string;
  parentId: string;
  childId: string;
  /**
   * How the line is drawn. `stack` and `spread` are reporting lines and come
   * out of the tidy tree; `dotted` is an extra relationship between two cards
   * that are already placed, and never moves either of them.
   */
  kind: 'stack' | 'spread' | 'dotted';
  /** Where the trunk runs. Only meaningful for a stacked edge. */
  trunkX: number;
  /** Which side of the card the stub enters. */
  side: 'left' | 'right';
}

export interface LayoutResult {
  positions: Map<string, Placement>;
  /** Ids rendered this pass, in draw order. */
  visibleIds: string[];
  /** Ids whose children exist but are hidden. */
  collapsedWithChildren: Set<string>;
  edges: LayoutEdge[];
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

export interface LayoutOptions {
  /** Subtree to render. Defaults to the model's roots. */
  rootId?: string | null;
  collapsed: ReadonlySet<string>;
  /**
   * Ids the filters left standing, or null for no filter. Anyone outside it is
   * not drawn at all — that is the difference between filtering and
   * highlighting, and the whole point of the canvas it gives back.
   */
  keep?: ReadonlySet<string> | null;
}

/** A laid-out subtree, positioned from x = 0. */
interface Block {
  width: number;
  positions: Map<string, Placement>;
  edges: LayoutEdge[];
}

/**
 * Top-down layout that keeps a wide org narrow, the way Rippling's chart does.
 *
 * A manager with more than two reports does not spread them across the canvas.
 * The ones that are collapsed — the great majority, most of the time — are
 * dealt into two columns underneath, so twelve reports cost two card widths
 * instead of twelve. Expand one of them and it steps out to the side, where it
 * has the room its own reports need.
 *
 * The alternative, a tidy Reingold-Tilford tree, makes the fully expanded
 * company about 90,000px wide, which cannot be framed at a readable zoom. This
 * trades width for height, and height is the direction you can scroll.
 */
export function layoutOrg(model: OrgModel, options: LayoutOptions): LayoutResult {
  const allRoots = options.rootId && model.byId.has(options.rootId) ? [options.rootId] : model.rootIds;
  const roots = options.keep ? allRoots.filter((id) => options.keep?.has(id)) : allRoots;

  const positions = new Map<string, Placement>();
  const visibleIds: string[] = [];
  const collapsedWithChildren = new Set<string>();
  const edges: LayoutEdge[] = [];

  const keep = options.keep ?? null;
  const childrenOf = (id: string): string[] => {
    const all = model.childIds.get(id) ?? [];
    const children = keep ? all.filter((child) => keep.has(child)) : all;
    if (children.length > 0 && options.collapsed.has(id)) {
      collapsedWithChildren.add(id);
      return [];
    }
    return children;
  };

  /** A report is stackable when it is not showing reports of its own. */
  const isStackable = (id: string): boolean => childrenOf(id).length === 0;

  const layoutSubtree = (id: string, y: number): Block => {
    const children = childrenOf(id);
    if (children.length === 0) {
      return { width: CARD_WIDTH, positions: new Map([[id, { x: 0, y }]]), edges: [] };
    }

    const stacked = children.filter(isStackable);
    const spread = children.filter((child) => !isStackable(child));
    const childY = y + ROW_PITCH;

    const blocks: Block[] = [];
    if (stacked.length > STACK_THRESHOLD) blocks.push(stackBlock(id, stacked, childY));
    else for (const child of stacked) blocks.push(layoutSubtree(child, childY));
    for (const child of spread) blocks.push(layoutSubtree(child, childY));

    // Lay the blocks out left to right, stack first.
    const merged = new Map<string, Placement>();
    const mergedEdges: LayoutEdge[] = [];
    let cursor = 0;
    for (const block of blocks) {
      for (const [key, placement] of block.positions) {
        merged.set(key, { x: placement.x + cursor, y: placement.y });
      }
      for (const edge of block.edges) mergedEdges.push({ ...edge, trunkX: edge.trunkX + cursor });
      cursor += block.width + BLOCK_GAP;
    }
    const childrenWidth = Math.max(0, cursor - BLOCK_GAP);

    /*
     * Centred over the cards of its direct reports, not over the whole subtree.
     * A manager belongs above the row it manages; centring over everything
     * below pushes them away from the people they actually manage as the
     * grandchildren fan out.
     */
    const spans = children.map((child) => merged.get(child) as Placement);
    const left = Math.min(...spans.map((placement) => placement.x));
    const right = Math.max(...spans.map((placement) => placement.x + CARD_WIDTH));
    let selfX = (left + right) / 2 - CARD_WIDTH / 2;

    // Nothing may sit at a negative offset: the block starts at zero.
    const shift = Math.max(0, -selfX);
    if (shift > 0) {
      for (const [key, placement] of merged) merged.set(key, { x: placement.x + shift, y: placement.y });
      for (const edge of mergedEdges) edge.trunkX += shift;
      selfX += shift;
    }

    merged.set(id, { x: selfX, y });
    for (const child of children) {
      if (mergedEdges.some((edge) => edge.childId === child)) continue;
      // A report with a place of its own is joined from above, so its "trunk"
      // is simply its own centre — the same path builder, one corner shorter.
      const placement = merged.get(child) as Placement;
      mergedEdges.push({
        id: `e-${id}-${child}`,
        parentId: id,
        childId: child,
        kind: 'spread',
        trunkX: placement.x + CARD_WIDTH / 2,
        side: 'left',
      });
    }

    return {
      width: Math.max(childrenWidth + shift, selfX + CARD_WIDTH),
      positions: merged,
      edges: mergedEdges,
    };
  };

  /** The reports that are not showing reports of their own, dealt into columns. */
  const stackBlock = (parentId: string, ids: string[], y: number): Block => {
    const rows = Math.ceil(ids.length / STACK_COLUMNS);
    const positions = new Map<string, Placement>();
    const edges: LayoutEdge[] = [];
    const trunkX = CARD_WIDTH + COLUMN_GAP / 2;

    ids.forEach((id, index) => {
      const column = Math.floor(index / rows);
      const row = index % rows;
      positions.set(id, {
        x: column * (CARD_WIDTH + COLUMN_GAP),
        y: y + row * STACK_PITCH,
      });
      edges.push({
        id: `e-${parentId}-${id}`,
        parentId,
        childId: id,
        kind: 'stack',
        trunkX,
        // The trunk runs between the columns, so the first column is entered
        // from its right edge and the second from its left.
        side: column === 0 ? 'right' : 'left',
      });
    });

    const columns = Math.min(STACK_COLUMNS, ids.length);
    return { width: columns * CARD_WIDTH + (columns - 1) * COLUMN_GAP, positions, edges };
  };

  let xOffset = 0;
  for (const rootId of roots) {
    const block = layoutSubtree(rootId, 0);
    for (const [id, placement] of block.positions) {
      positions.set(id, { x: placement.x + xOffset, y: placement.y });
      visibleIds.push(id);
    }
    for (const edge of block.edges) edges.push({ ...edge, trunkX: edge.trunkX + xOffset });
    xOffset += block.width + ROOT_TREE_GAP;
  }

  /*
   * Dotted lines last, and only between two cards that both survived: the
   * layout is finished by now, so this cannot move anything — which is the
   * whole promise of a dotted line. Somebody collapsed or filtered out simply
   * has no line drawn to them, rather than the line being drawn to nowhere.
   */
  const drawn = new Set(visibleIds);
  for (const id of visibleIds) {
    for (const managerId of model.byId.get(id)?.dottedManagerIds ?? []) {
      if (!drawn.has(managerId)) continue;
      edges.push({
        id: `dotted:${managerId}:${id}`,
        parentId: managerId,
        childId: id,
        kind: 'dotted',
        trunkX: 0,
        /*
         * Which side of *this* card the line enters, so it always faces the other
         * end: somebody to the left of who they answer to is entered from the
         * right. Getting this backwards sends the line the long way around the
         * card, which reads as a mistake rather than a relationship.
         */
        side: (positions.get(id)?.x ?? 0) < (positions.get(managerId)?.x ?? 0) ? 'right' : 'left',
      });
    }
  }

  return { positions, visibleIds, collapsedWithChildren, edges, bounds: boundsOf(positions) };
}

function boundsOf(positions: Map<string, Placement>): LayoutResult['bounds'] {
  if (positions.size === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const { x, y } of positions.values()) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x + CARD_WIDTH);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y + CARD_HEIGHT);
  }
  return { minX, maxX, minY, maxY };
}

/** Which node (if any) sits under a point in flow coordinates. */
export function hitTest(
  positions: Map<string, Placement>,
  point: { x: number; y: number },
  exclude: ReadonlySet<string>,
): string | null {
  for (const [id, placement] of positions) {
    if (exclude.has(id)) continue;
    if (
      point.x >= placement.x &&
      point.x <= placement.x + CARD_WIDTH &&
      point.y >= placement.y &&
      point.y <= placement.y + CARD_HEIGHT
    ) {
      return id;
    }
  }
  return null;
}

/**
 * Everyone below `rootId`, plus the depth-1 layer, expanded; everything deeper
 * collapsed. Gives a readable first paint on a 300+ person org.
 */
export function defaultCollapsed(model: OrgModel, expandToDepth = 2): Set<string> {
  const collapsed = new Set<string>();
  for (const [id, depth] of model.depth) {
    if (depth >= expandToDepth && (model.childIds.get(id)?.length ?? 0) > 0) collapsed.add(id);
  }
  return collapsed;
}
